/**
 * The floating chrome's shared shell: the floating material, opaque (D358).
 *
 * This was the refracting shell, `SvgGlass`: a mirror canvas that copied the
 * graph after every paint and bent it at the rim through an SVG
 * feDisplacementMap, under a white sheen and, on a side pane, an inner shade —
 * the liquid glass. D357 replaced that with a translucent fill over a light
 * blur, and D358 with the design's card, which nothing shows through. What is
 * left here is the part that was never the lens: one element wearing `glass()`,
 * and the column's ground in its place when a pane is docked.
 *
 * Gone with the lens, and none of it is owed anything: the per-frame blit, which
 * was the largest single item in the frame profile (docs/performance-audit.md,
 * finding 1); the lens map built when the main thread was idle; the frame-budget
 * meter that dropped the bending on a slow machine; and the resize backstop that
 * kept the mirror's backing store the size of its box. So is the
 * reduced-transparency path, which swapped the frost for an opaque surface:
 * every surface is opaque now, for everybody.
 */

import type { CSSProperties, ReactNode } from 'react'
import { glass } from './glass'

export function GlassShell({
  as = 'div',
  ariaLabel,
  radius = 'var(--r-glass)',
  lift = 'card',
  solid,
  style,
  contentStyle,
  children,
}: {
  /** `aside` for the landmark panes; plain `div` for chrome. */
  as?: 'div' | 'aside'
  ariaLabel?: string
  /** Corner radius, in px or as a token. */
  radius?: number | string
  /** A card takes the material's drop; flat chrome — the tool dock — takes
   *  none. See `glass()`. */
  lift?: 'card' | 'flat'
  /** A ground in place of the floating material, for a column docked against
   *  the window's edge (D353): no edge, no drop — a surface beside the field
   *  rather than a sheet over it. A floating surface never passes it: over the
   *  field, the floating material is the one material. */
  solid?: string
  /** Outer shell overrides: position, insets, size. Merged last, so a caller
   *  may also override the shell's own visual defaults. */
  style?: CSSProperties
  /** Content wrapper overrides — defaults to a min-height-0 flex column. */
  contentStyle?: CSSProperties
  children: ReactNode
}) {
  const Tag = as
  return (
    <Tag
      aria-label={ariaLabel}
      style={{
        position: 'relative',
        borderRadius: radius,
        ...(solid !== undefined ? { background: solid } : glass(lift)),
        // `clip`, not `hidden`: hidden makes the shell a scroll container, and
        // any child overhang past the right/bottom edge (the inspector close
        // button's -6px optical margin) becomes scrollable overflow a
        // focus-reveal can shear the content by. clip paints identically and
        // forbids all scrolling, programmatic included — the shell can never be
        // shifted off its own content.
        overflow: 'clip',
        ...style,
      }}
    >
      {/* Its own stacking context, as it was when the lens's layers sat under
          it: the shell makes none of its own, and a menu inside the pane stacks
          against the pane's contents, not against the page. */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          ...contentStyle,
        }}
      >
        {children}
      </div>
    </Tag>
  )
}

/** The side panes' shape of the shell: an `aside` landmark. Docked, it wears its
 *  column's ground (`solid`, D353); on a phone it floats, a card of the floating
 *  material. */
export function GlassPane({
  ariaLabel,
  shellStyle,
  radius = 'var(--r-glass)',
  solid,
  children,
}: {
  ariaLabel: string
  shellStyle: CSSProperties
  /** Corner radius. Defaults to the material's; a docked pane flush to the
   *  window edge passes 0 so it reads as a column, not a card. */
  radius?: number | string
  /** The docked column's own ground, in place of the floating material (D353). */
  solid?: string
  children: ReactNode
}) {
  return (
    <GlassShell as="aside" ariaLabel={ariaLabel} radius={radius} solid={solid} style={shellStyle}>
      {children}
    </GlassShell>
  )
}
